type ReviewPrefetchCacheEntry = {
  expiresAt?: number;
  promise?: Promise<unknown>;
  value?: unknown;
};

const REVIEW_PREFETCH_TTL_MS = 5 * 60 * 1000;

// Keep hover and focus responses out of Redux until the reviews page consumes
// them. This avoids development middleware scanning large review arrays.
const reviewPrefetchCache = new Map<string, ReviewPrefetchCacheEntry>();

export const createReviewPrefetchKey = (businessId: string) =>
  `trustpilot:regular:${businessId}`;

/**
 * Coalesces hover/focus and reviews-page requests, then keeps the successful
 * response briefly so navigating after a hover does not download it again.
 */
export const getOrCreateReviewPrefetch = async <T>(
  key: string,
  load: () => Promise<T>,
): Promise<T> => {
  const cached = reviewPrefetchCache.get(key);

  if (
    cached?.value !== undefined &&
    cached.expiresAt !== undefined &&
    cached.expiresAt > Date.now()
  ) {
    return cached.value as T;
  }

  if (cached?.promise) {
    return cached.promise as Promise<T>;
  }

  const promise = load()
    .then((value) => {
      reviewPrefetchCache.set(key, {
        value,
        expiresAt: Date.now() + REVIEW_PREFETCH_TTL_MS,
      });
      return value;
    })
    .catch((error) => {
      if (reviewPrefetchCache.get(key)?.promise === promise) {
        reviewPrefetchCache.delete(key);
      }
      throw error;
    });

  reviewPrefetchCache.set(key, { promise });
  return promise;
};
